import React, { useState, useEffect } from "react";
import "./DynamicWorkHistoryTable.css";

interface WorkEntry {
  startDate: string;
  endDate: string;
  jobTitle: string;
  institutionName: string;
  cityTown: string;
}

interface DynamicWorkHistoryTableProps {
  value: { [key: string]: WorkEntry };
  onChange: (value: { [key: string]: WorkEntry }) => void;
  required?: boolean;
}

const DynamicWorkHistoryTable: React.FC<DynamicWorkHistoryTableProps> = ({
  value,
  onChange,
  required = false,
}) => {
  const [entries, setEntries] = useState<{ [key: string]: WorkEntry }>(
    value || {}
  );

  useEffect(() => {
    setEntries(value || {});
  }, [value]);

  // Add keyboard support for Ctrl+Enter to add new entry
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === "Enter") {
        event.preventDefault();
        addEntry();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const addEntry = () => {
    const entryCount = Object.keys(entries).length;
    const newKey = `workHistory${entryCount + 1}`;

    const newEntry: WorkEntry = {
      startDate: "",
      endDate: "",
      jobTitle: "",
      institutionName: "",
      cityTown: "",
    };

    const updatedEntries = { ...entries, [newKey]: newEntry };
    setEntries(updatedEntries);
    onChange(updatedEntries);
  };

  const removeEntry = (key: string) => {
    const { [key]: removed, ...remainingEntries } = entries;

    // Renumber the remaining entries
    const renumberedEntries: { [key: string]: WorkEntry } = {};
    const remainingKeys = Object.keys(remainingEntries).sort();

    remainingKeys.forEach((oldKey, index) => {
      const newKey = `workHistory${index + 1}`;
      renumberedEntries[newKey] = remainingEntries[oldKey];
    });

    setEntries(renumberedEntries);
    onChange(renumberedEntries);
  };

  const updateEntry = (key: string, field: keyof WorkEntry, value: string) => {
    const updatedEntries = {
      ...entries,
      [key]: { ...entries[key], [field]: value },
    };
    setEntries(updatedEntries);
    onChange(updatedEntries);
  };

  // Work history date validation function (yyyy-mm format)
  const isValidWorkDate = (dateString: string): boolean => {
    if (!dateString) return true; // Empty dates are valid (for optional fields)

    // Check if the date string matches yyyy-mm format
    const dateRegex = /^\d{4}-\d{2}$/;
    if (!dateRegex.test(dateString)) return false;

    const year = parseInt(dateString.split("-")[0]);
    const month = parseInt(dateString.split("-")[1]);

    // Basic range checks
    if (year < 1900 || year > new Date().getFullYear() + 10) return false;
    if (month < 1 || month > 12) return false;

    return true;
  };

  const entryKeys = Object.keys(entries).sort();

  return (
    <div className="dynamic-work-history-table">
      <div className="table-header">
        <h4>Work History for Last 10 Years</h4>
        <div className="header-actions">
          <button
            type="button"
            className="add-row-btn"
            onClick={addEntry}
            title="Add new work history entry (Ctrl+Enter)"
          >
            + Add
          </button>
        </div>
      </div>

      {entryKeys.length === 0 ? (
        <div className="empty-state">
          <button type="button" className="add-first-btn" onClick={addEntry}>
            Add First Work Entry
          </button>
        </div>
      ) : (
        <div className="entries-container">
          {entryKeys.map((key, index) => (
            <div key={key} className="entry-card">
              <div className="entry-header">
                <div className="entry-number">
                  <span className="entry-badge">{index + 1}</span>
                  <h5>Work Entry {index + 1}</h5>
                </div>
                <button
                  type="button"
                  className="remove-btn"
                  onClick={() => removeEntry(key)}
                  title="Remove this work entry"
                >
                  ×
                </button>
              </div>
              
              <div className="entry-form">
                <div className="form-row form-row-1">
                  <div className="field-group">
                    <label>Start Date (yyyy-mm)</label>
                    <input
                      type="text"
                      value={entries[key].startDate}
                      onChange={(e) =>
                        updateEntry(key, "startDate", e.target.value)
                      }
                      placeholder="2020-01"
                      className={
                        !isValidWorkDate(entries[key].startDate) &&
                        entries[key].startDate
                          ? "invalid"
                          : ""
                      }
                    />
                    {!isValidWorkDate(entries[key].startDate) &&
                      entries[key].startDate && (
                        <small className="error-text">
                          Invalid format (yyyy-mm)
                        </small>
                      )}
                  </div>
                  
                  <div className="field-group">
                    <label>End Date (yyyy-mm)</label>
                    <input
                      type="text"
                      value={entries[key].endDate}
                      onChange={(e) =>
                        updateEntry(key, "endDate", e.target.value)
                      }
                      placeholder="2024-12"
                      className={
                        !isValidWorkDate(entries[key].endDate) &&
                        entries[key].endDate
                          ? "invalid"
                          : ""
                      }
                    />
                    {!isValidWorkDate(entries[key].endDate) &&
                      entries[key].endDate && (
                        <small className="error-text">
                          Invalid format (yyyy-mm)
                        </small>
                      )}
                  </div>
                  
                  <div className="field-group">
                    <label>Title of Job</label>
                    <input
                      type="text"
                      value={entries[key].jobTitle}
                      onChange={(e) =>
                        updateEntry(key, "jobTitle", e.target.value)
                      }
                      placeholder="Software Engineer"
                    />
                  </div>
                </div>
                
                <div className="form-row form-row-2">
                  <div className="field-group">
                    <label>Name of Institution</label>
                    <input
                      type="text"
                      value={entries[key].institutionName}
                      onChange={(e) =>
                        updateEntry(key, "institutionName", e.target.value)
                      }
                      placeholder="ABC Company"
                    />
                  </div>
                  
                  <div className="field-group">
                    <label>City/Town</label>
                    <input
                      type="text"
                      value={entries[key].cityTown}
                      onChange={(e) =>
                        updateEntry(key, "cityTown", e.target.value)
                      }
                      placeholder="Toronto"
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add another button at the bottom */}
      {entryKeys.length > 0 && (
        <div className="table-footer">
          <p className="add-hint">Need to add more work history entries?</p>
          <button
            type="button"
            className="add-another-btn"
            onClick={addEntry}
          >
            + Add Another Work Entry
          </button>
        </div>
      )}

      {required && entryKeys.length === 0 && (
        <small className="error-text">
          At least one work history entry is required
        </small>
      )}
    </div>
  );
};

export default DynamicWorkHistoryTable;
